import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ActionIcon, Box, Divider, Grid, Group, Menu, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBattery,
  IconClipboardPlus,
  IconDotsVertical,
  IconMailForward,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconPrinter,
  IconShieldCheck,
  IconUserCheck,
  IconUserOff
} from '@tabler/icons-react';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { selectPatientOverviewDetails } from '@/store/selectors/patientProfilePageSelectors';
import { selectPatientDetails } from '@/store/selectors/transmissionReportPatientSelectors';
import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { formatDateWithAge } from '@/utils/formatDate.ts';
import { getMrnLabel } from '@/utils/getMrnLabel';
import { patientStatusEnum, patientStatusLabel } from '@/utils/patientStatus';
import usePatientStatusTooltip from '@/hooks/usePatientStatusTooltip';
import { usePrintLetters } from '@/hooks/usePrintLetters';
import EditIcon from '@/assets/icons/EditIcon';
import InfoIcon from '@/assets/icons/InfoIcon';

import { PatientStatus } from '@/pages/physician/analytics/constants';
import DeviceStatus from '@/pages/physician/PatientOverviewPage/DeviceStatus';
import { EnrollmentGapSummary } from '@/pages/physician/PatientOverviewPage/EnrollmentGapIndicator';
import InsuranceLookupModal from '@/pages/physician/PatientOverviewPage/InsuranceLookupModal';
import PatientEditModal from '@/pages/physician/PatientOverviewPage/PatientOverview/patientEditModal';
import { primaryPhoneDisplay } from '@/pages/physician/PatientOverviewPage/PatientOverview/patientPhones';
import { CreateTaskModal } from '@/pages/physician/PatientOverviewPage/PatientTasksList/CreateTaskModal';
import {
  loadClinicScheduleTemplateId,
  loadScheduleLetterEligibility,
  scheduleLetterAvailable
} from '@/pages/physician/PatientOverviewPage/scheduleLetter';
import PrintLetterModal from '@/pages/physician/PatientOverviewPage/ScheduleList/PrintLetterModal';
import SendScheduleLetterModal from '@/pages/physician/PatientOverviewPage/SendScheduleLetterModal';
import { showsInsuranceLookup } from '@/types/insurance';
import { PatientActiveStatusEnum } from '@/types/patient';
import LabeledField from '@/ui/LabeledField';

import { ChangeInactiveReasonModal } from './ChangeInactiveReasonModal';
import { DeactivatePatientModal } from './DeactivatePatientModal';
import styles from './DetailsSection.module.scss';
import { EriTrackingActionModal } from './EriTrackingActionModal';
import { eriActionTargets, eriEligibleDevices } from './eriTrackingActions';
import { PausePatientModal } from './PausePatientModal';
import { ReactivatePatientModal } from './ReactivatePatientModal';

const PatientField = (props: React.ComponentProps<typeof LabeledField>) => (
  <LabeledField labelVariant="sentence" editOnHover {...props} />
);

interface DetailsSectionProps {
  patientId: string;
}

const TooltipItems = memo(({ items }: { items: { label: string; value: string }[] }) => (
  <Text size="xs" c="white">
    {items.map(({ label, value }) => (
      <span key={label}>
        <b>{label}:</b> {value}
        <br />
      </span>
    ))}
  </Text>
));

const DetailsSection = memo(({ patientId }: DetailsSectionProps) => {
  const patient = useSelector((state: RootState) => selectPatientDetails(state, patientId));
  const overview = useSelector((state: RootState) =>
    selectPatientOverviewDetails(state, patientId)
  );
  const isInternalUser = useAppSelector(
    (state) => state.auth.user?.role?.includes('internal') ?? false
  );

  const [updatePatientDetails] = useUpdatePatientMutation();

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isInactiveReasonOpen, setIsInactiveReasonOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isInsuranceOpen, setIsInsuranceOpen] = useState(false);
  const [isScheduleLetterOpen, setIsScheduleLetterOpen] = useState(false);
  const [isPrintScheduleOpen, setIsPrintScheduleOpen] = useState(false);
  const [eriMode, setEriMode] = useState<'start' | 'stop' | null>(null);
  const [scheduleLetterEligible, setScheduleLetterEligible] = useState(false);

  const statusLabel = patientStatusLabel(patient?.status);
  const isInactive = statusLabel === 'Inactive';
  const isPaused = statusLabel === 'Paused';

  const handleSetActive = async (message: string) => {
    try {
      await updatePatientDetails({
        patient: {
          id: patientId,
          status: PatientStatus.Active,
          statusReason: null,
          statusStartDate: null,
          statusEndDate: null
        }
      }).unwrap();
      notifications.show({
        title: message,
        message: 'Patient has been set to active.',
        color: 'teal'
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient status.',
        color: 'red'
      });
    }
  };

  const { data: fullPatient } = useGetPatientQuery(patientId, { skip: !patientId });
  const eriTargets = useMemo(() => {
    const eligible = eriEligibleDevices(fullPatient?.matchDevices ?? patient?.matchDevices);
    return eriActionTargets(eligible);
  }, [fullPatient?.matchDevices, patient?.matchDevices]);
  const clinicId = fullPatient?.clinic?.id ?? '';
  const { printLetters } = usePrintLetters(
    clinicId,
    fullPatient?.clinic?.parent?.id ?? fullPatient?.clinic?.parentId
  );

  useEffect(() => {
    if (!isInternalUser || !patientId || !clinicId) {
      setScheduleLetterEligible(false);
      return;
    }
    let cancelled = false;
    setScheduleLetterEligible(false);
    loadClinicScheduleTemplateId(clinicId)
      .then((templateId) => {
        if (cancelled || !templateId) {
          if (!cancelled) setScheduleLetterEligible(false);
          return null;
        }
        return loadScheduleLetterEligibility(patientId);
      })
      .then((row) => {
        if (cancelled || !row) return;
        setScheduleLetterEligible(scheduleLetterAvailable(row, fullPatient?.isRemoteMonitored));
      })
      .catch(() => {
        if (!cancelled) setScheduleLetterEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, fullPatient?.isRemoteMonitored, isInternalUser, patientId]);

  const tooltipData = usePatientStatusTooltip(patient);

  const openDemographics = () => setIsEditOpen(true);

  const getStatusClassName = useCallback((status: string) => {
    const value = status?.toLowerCase();
    if (value === 'active') return styles.statusActive;
    if (value === 'pending') return styles.statusPending;
    return styles.statusInactive;
  }, []);

  const toTitleCase = (str: string) => {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getPatientStatus = (status?: string) => patientStatusEnum(status);

  const renderStatusTooltip = useCallback(() => {
    if (!tooltipData.shouldShow) return null;

    return (
      <Tooltip
        label={
          <Box>
            <TooltipItems items={tooltipData.items} />
          </Box>
        }
        multiline
        position="bottom-start"
        withArrow
        classNames={{ tooltip: styles.tooltip }}>
        <div className={styles.infoIconWrapper}>
          <InfoIcon />
        </div>
      </Tooltip>
    );
  }, [tooltipData]);

  if (!patient) return <div>Error loading Patient Details</div>;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.identity}>
          <div className={styles.nameRow}>
            <Title
              order={1}
              className={styles.patientName}
              style={{
                fontSize: 32,
                lineHeight: 1.05,
                fontWeight: 600,
                fontFamily: "'Libre Caslon Text', Georgia, serif"
              }}>
              {patient.givenName} {patient.familyName}
            </Title>
            <button
              type="button"
              className={styles.nameEdit}
              onClick={openDemographics}
              aria-label={`Edit ${patient.givenName} ${patient.familyName}`}>
              <EditIcon />
            </button>
          </div>
          <Box className={styles.clinicBlock}>
            {fullPatient?.clinic?.parent?.name &&
              fullPatient.clinic.parent.name !== patient.clinic?.name && (
                <Text className={styles.clinicName}>{fullPatient.clinic.parent.name}</Text>
              )}
            {patient.clinic?.name && (
              <Text size="xs" c="dimmed">
                Clinic: {patient.clinic.name}
              </Text>
            )}
          </Box>
        </div>
        <Group ml="auto" gap={8} wrap="nowrap" align="center">
          <EnrollmentGapSummary />
          <DeviceStatus devices={fullPatient?.matchDevices ?? patient.matchDevices} />
          <Menu position="bottom-end" withinPortal shadow="md" width={240}>
            <Menu.Target>
              <Tooltip label="Actions" withArrow>
                <ActionIcon variant="light" color="plum" size="lg" aria-label="Patient actions">
                  <IconDotsVertical size={18} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Actions</Menu.Label>
              {isInternalUser && (
                <Menu.Item
                  leftSection={<IconClipboardPlus size={16} />}
                  onClick={() => setIsCreateTaskModalOpen(true)}>
                  Create Task
                </Menu.Item>
              )}
              {isInternalUser && showsInsuranceLookup(fullPatient?.clinic?.billingType) && (
                <Menu.Item
                  leftSection={<IconShieldCheck size={16} />}
                  onClick={() => setIsInsuranceOpen(true)}>
                  Add & Edit Insurance
                </Menu.Item>
              )}
              {isInternalUser && scheduleLetterEligible && (
                <Menu.Item
                  leftSection={<IconMailForward size={16} />}
                  onClick={() => setIsScheduleLetterOpen(true)}>
                  Send Schedule Letter
                </Menu.Item>
              )}
              {isInternalUser && eriTargets.start.length > 0 && (
                <Menu.Item
                  leftSection={<IconBattery size={16} />}
                  onClick={() => setEriMode('start')}>
                  Start ERI Tracking
                </Menu.Item>
              )}
              {isInternalUser && eriTargets.stop.length > 0 && (
                <Menu.Item
                  leftSection={<IconBattery size={16} />}
                  onClick={() => setEriMode('stop')}>
                  Stop ERI Tracking
                </Menu.Item>
              )}
              {getPatientStatus(patient.status) === PatientActiveStatusEnum.Active && (
                <Menu.Item
                  leftSection={<IconPlayerPause size={16} />}
                  onClick={() => setIsPauseModalOpen(true)}>
                  Pause Patient
                </Menu.Item>
              )}
              {isPaused && (
                <Menu.Item
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={() => handleSetActive('Patient unpaused')}>
                  Unpause Patient
                </Menu.Item>
              )}
              {printLetters && fullPatient && (
                <Menu.Item
                  leftSection={<IconPrinter size={16} />}
                  onClick={() => setIsPrintScheduleOpen(true)}>
                  Print Schedule
                </Menu.Item>
              )}
              {isInactive ? (
                <>
                  <Menu.Item
                    leftSection={<IconPencil size={16} />}
                    onClick={() => setIsInactiveReasonOpen(true)}>
                    Change Inactive Reason
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconUserCheck size={16} />}
                    onClick={() => setIsReactivateModalOpen(true)}>
                    Activate Patient
                  </Menu.Item>
                </>
              ) : (
                <Menu.Item
                  leftSection={<IconUserOff size={16} />}
                  color="red"
                  onClick={() => setIsDeactivateModalOpen(true)}>
                  Deactivate Patient
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>

      <Grid pt="md">
        <Grid.Col span={{ xs: 12, sm: 4, md: 2 }}>
          <PatientField
            label="Status"
            value={
              <div className={styles.statusWrapper}>
                <Text className={styles.statusText}>
                  {toTitleCase(getPatientStatus(patient.status))}
                </Text>
                {renderStatusTooltip()}
              </div>
            }
            valueClassName={getStatusClassName(getPatientStatus(patient.status))}
          />
        </Grid.Col>
        {isInternalUser && (
          <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
            <PatientField label="Rhythm Id" value={patient.rhythmId} />
          </Grid.Col>
        )}
        <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
          <PatientField
            label={getMrnLabel(patient.clinic?.useMpiLabel)}
            value={patient.mrn2}
            onEdit={openDemographics}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 3 }}>
          <PatientField
            label="DOB"
            value={formatDateWithAge(patient.birthDate, 'MM/DD/YYYY')}
            onEdit={openDemographics}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
          <PatientField
            label="Sex"
            value={patient.sex ? toTitleCase(patient.sex) : undefined}
            onEdit={openDemographics}
            showEditIcon
          />
        </Grid.Col>
      </Grid>

      <Divider my="md" />

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <PatientField
            label="Address 1"
            value={overview?.address1 ?? '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <PatientField
            label="Address 2"
            value={overview?.address2 ?? '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 4, md: 2 }}>
          <PatientField
            label="City"
            value={overview?.city ?? '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ base: 3, sm: 4, md: 2 }}>
          <PatientField
            label="State"
            value={overview?.state ?? '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ base: 3, sm: 4, md: 2 }}>
          <PatientField
            label="Zip"
            value={overview?.zip ?? '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <PatientField
            label="Primary Phone"
            value={primaryPhoneDisplay(overview) || '-'}
            onEdit={() => setIsEditOpen(true)}
            showEditIcon
          />
        </Grid.Col>
      </Grid>

      {overview && (
        <PatientEditModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          patient={overview}
          mrnLabel={getMrnLabel(patient.clinic?.useMpiLabel)}
        />
      )}

      {fullPatient && (
        <CreateTaskModal
          opened={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          patientId={patientId}
          patientName={`${fullPatient.givenName} ${fullPatient.familyName}`}
          organizationId={fullPatient.clinic?.id ?? ''}
          devices={fullPatient.matchDevices ?? []}
          isPatientHfMonitored={Boolean(fullPatient.recentHeartFailureStatus)}
          patientStatus={fullPatient.status}
        />
      )}
      <ReactivatePatientModal
        opened={isReactivateModalOpen}
        onClose={() => setIsReactivateModalOpen(false)}
        patientId={patientId}
        patientName={
          fullPatient ? `${fullPatient.givenName} ${fullPatient.familyName}` : 'this patient'
        }
        devices={fullPatient?.matchDevices ?? []}
      />
      <ChangeInactiveReasonModal
        opened={isInactiveReasonOpen}
        onClose={() => setIsInactiveReasonOpen(false)}
        patientId={patientId}
        patientName={
          fullPatient ? `${fullPatient.givenName} ${fullPatient.familyName}` : 'this patient'
        }
        currentReason={fullPatient?.statusReason ?? patient?.statusReason}
        currentExplanation={fullPatient?.statusOtherReason ?? patient?.statusOtherReason}
      />
      <DeactivatePatientModal
        opened={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        patientId={patientId}
        patientName={
          fullPatient ? `${fullPatient.givenName} ${fullPatient.familyName}` : 'this patient'
        }
        devices={fullPatient?.matchDevices ?? []}
      />
      <InsuranceLookupModal
        opened={isInsuranceOpen}
        onClose={() => setIsInsuranceOpen(false)}
        patientId={patientId}
      />
      {printLetters && fullPatient && (
        <PrintLetterModal
          opened={isPrintScheduleOpen}
          onClose={() => setIsPrintScheduleOpen(false)}
          patient={fullPatient}
          printLetters={printLetters}
        />
      )}
      <SendScheduleLetterModal
        opened={isScheduleLetterOpen}
        onClose={() => setIsScheduleLetterOpen(false)}
        onSent={() => setScheduleLetterEligible(false)}
        patientId={patientId}
        clinicId={fullPatient?.clinic?.id ?? ''}
        patient={{
          givenName: fullPatient?.givenName,
          familyName: fullPatient?.familyName,
          isRemoteMonitored: fullPatient?.isRemoteMonitored,
          address: fullPatient?.address,
          clinic: { name: fullPatient?.clinic?.name }
        }}
      />
      <EriTrackingActionModal
        opened={eriMode !== null}
        mode={eriMode ?? 'start'}
        devices={eriMode === 'stop' ? eriTargets.stop : eriTargets.start}
        patientId={patientId}
        onClose={() => setEriMode(null)}
      />
      <PausePatientModal
        opened={isPauseModalOpen}
        onClose={() => setIsPauseModalOpen(false)}
        patientId={patientId}
        patientName={
          fullPatient ? `${fullPatient.givenName} ${fullPatient.familyName}` : 'this patient'
        }
      />
    </div>
  );
});

DetailsSection.displayName = 'DetailsSection';

export default DetailsSection;
